import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { IAssembly } from 'src/interfaces';
import { queryKeys } from 'src/utils/queryKeys';

import { requestCCApi } from '../../../utils/axios-utils';

export const useUpdateAssembly = () => {
  return useMutation({
    mutationFn: updateAssembly,
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKeys.assemblies.invalidateAll());
      await queryClient.invalidateQueries(
        queryKeys.assemblyListing.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.assemblyTags.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.viewAssemblyTagsWithAssembliesCount.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.bulkReplaceEligibleAssemblyIds.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.scheduleAssembliesSearch.invalidateAll(),
      );
      await queryClient.invalidateQueries(queryKeys.components.invalidateAll());
      await queryClient.invalidateQueries(
        queryKeys.componentListing.invalidateAll(),
      );
    },
  });
};

const updateAssembly = async (
  payload: Partial<IAssembly> & { assemblyName?: string },
) => {
  const narrativeName = payload.name ?? payload.assemblyName;
  const data = {
    ...(payload.name && { name: payload.name }),
    ...(narrativeName && {
      narrative: (
        narrativeGenerators[COLLECTIONS.ASSEMBLIES][USER_ACTIONS.UPDATE] as (
          assemblyName: string,
        ) => string
      )(narrativeName),
    }),
    ...(payload.status && { status: payload.status }),
    ...(payload.config && { config: payload.config }),
    ...(payload.assembly_tag_id && {
      assembly_tag_id: payload.assembly_tag_id,
    }),
  };

  const response = await requestCCApi({
    url: `/assemblies/${payload.id}`,
    method: 'PATCH',
    data: { data },
  });

  return response.data;
};

export const useUpdateAssemblyWithAssemblyTags = (
  onSuccessCallback?: () => void,
) => {
  return useMutation({
    mutationFn: updateAssemblyWithAssemblyTags,
    onSuccess: () => {
      queryClient.removeQueries(queryKeys.assemblies.invalidateAll());
      queryClient.removeQueries(queryKeys.assemblyListing.invalidateAll());
      queryClient.invalidateQueries(queryKeys.assemblyTags.invalidateAll());
      queryClient.invalidateQueries(
        queryKeys.viewAssemblyTagsWithAssembliesCount.invalidateAll(),
      );
      onSuccessCallback?.();
    },
  });
};

const updateAssemblyWithAssemblyTags = async (payload: IAssembly[]) => {
  const data = {
    data: payload.map((assembly) => ({
      resource: 'assemblies',
      method: 'PATCH',
      id: assembly.id,
      data: {
        ...(assembly.assembly_tag_id && {
          assembly_tag_id: assembly.assembly_tag_id,
        }),
      },
    })),
  };

  return requestCCApi({ url: '/transaction', method: 'POST', data });
};
